import { cx } from "@shared/components/untitledui/util/cx"
import type { CardStackSummary, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course, CourseCalendarTime } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type Notifier from "cc-frontend/services/notifier"
import { useRef } from "react"
import UnitsInHeader from "react-app/components/planner/card-stacks/lesson/UnitsInHeader"
import { useEmberService } from "react-app/providers/ember-service-context"
import UnifiedCardStackEditor from "../UnifiedCardStackEditor"
import AddToDayButton from "./AddToDayButton"
import CourseTitleBar from "./CourseTitleBar"
import { dayNoteAnnotation, dayOffAnnotation } from "./day-off-annotation"
import styles from "./InstructionalLesson.module.css"
import { session as dragSession } from "./lesson-drag-session"
import { studentLessonUrl } from "./lesson-links"
import type { RemoveClassTodayChoice } from "./lesson-menu-views/remove-class-today-flow"
import LessonDateAnnotations from "./LessonDateAnnotations"
import NonInstructionalMenu from "./NonInstructionalMenu"
import { useFocusedLessonScroll } from "./use-focused-lesson-scroll"
import { useLessonClipboard } from "./use-lesson-clipboard"

interface Props {
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  course: Course
  courseDate: CourseDateCombined
  units: Array<CardStackSummary> | null
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  modelsNeededForFirstEdit: ModelsNeededForFirstEdit
  /** Opens the edit-course modal for the given courseId. */
  editCourseTitleAndOrder: (courseId: string) => void
}

/**
 * A non-instructional course's slot: its name and its notes, with nothing to plan.
 * Notes use UnifiedCardStackEditor with no lesson title and only untitledCard cards.
 */
const NonInstructionalLesson = tracked(function ({
  lesson,
  course,
  courseDate,
  units,
  planbook,
  rotationCalendar,
  dateString,
  modelsNeededForFirstEdit,
  editCourseTitleAndOrder,
}: Props) {
  const slotRef = useRef<HTMLDivElement>(null)
  useFocusedLessonScroll(slotRef, course.id)

  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const courseCalendarEditor = useEmberService<CourseCalendarEditor>("course-calendar-editor")
  const notifier = useEmberService<Notifier>("notifier")
  const { canPaste, copyLesson, pasteLesson } = useLessonClipboard({
    lesson,
    course,
    planbook,
    modelsNeededForFirstEdit,
  })

  const canEditLesson = lesson.meta?.permissions?.includes("can-edit-plan") ?? false
  const hasNotes = !!lesson.attributes.cards && lesson.attributes.cards.length > 0

  // A drag of ANY lesson folds this slot down to its course bar. It is never a drop target — it
  // just gives back the vertical space its notes take, so a week of real targets fits on screen.
  const isAnyLessonDragging = dragSession().kind === "dragging"

  // Copying an empty slot would leave the user's clipboard holding nothing, silently.
  const copyNote = () => {
    if (!hasNotes) {
      notifier.warning("", {
        title: "There's nothing to copy!",
        message: "Click Add Note to add a note for today.",
      })
      return
    }
    copyLesson()
  }

  const addNote = () => lessonEditor.createUnifiedNote(modelsNeededForFirstEdit, lesson.id)

  const eraseNote = () => {
    if (courseDate.cardStackId) {
      lessonEditor.eraseLesson(course, dateString, courseDate.cardStackId, courseDate.timeCustom)
    }
  }

  const setCustomTime = (time: CourseCalendarTime | null) =>
    lessonEditor.setCustomTime(modelsNeededForFirstEdit, course, dateString, planbook.id, time)

  /**
   * Turns the class off for this day, pinning `note` to it. There is no lesson to bump or
   * delete, so the day only gains its annotations ("NO_LESSON"). The off marker and the note are
   * separate, so turning the class back on later leaves the note where it is.
   */
  const removeClassToday = ({ note }: RemoveClassTodayChoice) => {
    courseCalendarEditor.turnOffAndBumpDeleteOrNothing({
      dateAnnotation: dayOffAnnotation({ dateString }),
      noteAnnotation: note === null ? null : dayNoteAnnotation({ dateString, note }),
      course,
      planbook,
      cardStackId: courseDate.cardStackId,
      rotationCalendar,
      dateString,
      bumpOrDelete: "NO_LESSON",
    })
  }

  // Whether anything paints under the notes: a reader with no note to read gets an empty body,
  // and a drag collapses it to nothing. Both the bar above and the notes above have to agree
  // with this, or one of them rounds off a corner the card still continues past.
  const showsNotesBody =
    courseDate.showLesson && (hasNotes || canEditLesson) && !isAnyLessonDragging

  return (
    <div className="mb-3" data-test="non-instructional lesson" ref={slotRef}>
      {/* The course title bar is always here; under it goes the note, or the notes body. */}
      <CourseTitleBar
        course={course}
        courseDate={courseDate}
        planbook={planbook}
        rotationCalendar={rotationCalendar}
        setCustomTime={setCustomTime}
        hasBodyBelow={courseDate.hasVisibleAnnotations || showsNotesBody}
        parentCardStack={null}
        editCourseTitleAndOrder={editCourseTitleAndOrder}
        menu={
          <NonInstructionalMenu
            course={course}
            dateString={dateString}
            canEdit={canEditLesson}
            editCourseTitleAndOrder={editCourseTitleAndOrder}
            copyLesson={copyNote}
            pasteLesson={pasteLesson}
            canPasteLesson={canPaste}
            hasNote={hasNotes}
            eraseNote={eraseNote}
            removeClassToday={removeClassToday}
          />
        }
      />

      {courseDate.hasVisibleAnnotations && (
        <LessonDateAnnotations
          course={course}
          courseDate={courseDate}
          hasSlotBelow={showsNotesBody}
        />
      )}

      {courseDate.showLesson && (
        /* Folds shut mid-drag (grid-rows 1fr -> 0fr) with no floor to stop at — there is no lesson
           title down here to keep on screen. Kept mounted so an in-progress note survives, and so
           it comes back instantly when the drag ends. */
        <div
          className={cx(styles.bodyCollapser, isAnyLessonDragging && styles.collapsed)}
          data-test="non-instructional body"
        >
          <div className={cx("min-h-0", isAnyLessonDragging && "overflow-y-clip")}>
            {hasNotes ? (
              // No bottom padding: the last card's separator already owns 8px of white below it.
              <div className="rounded-b-md bg-white px-3" data-test="lesson notes">
                <UnifiedCardStackEditor
                  cardStack={lesson}
                  modelsNeededForFirstEdit={modelsNeededForFirstEdit}
                  isNonInstructional={true}
                  studentLessonUrl={studentLessonUrl(course.id, courseDate.dateString)}
                />
              </div>
            ) : (
              canEditLesson && (
                <div
                  className="set-course-color-vars take-course-background-color rounded-b-md"
                  data-color={course.attributes.color}
                >
                  <AddToDayButton label="Add Note" dataTest="add note" onPress={addNote} />
                </div>
              )
            )}
          </div>
        </div>
      )}
      <UnitsInHeader
        units={units}
        course={course}
        planbook={planbook}
        rotationCalendar={rotationCalendar}
        dateString={dateString}
        editCourseTitleAndOrder={editCourseTitleAndOrder}
        className="mb-4.5"
      />
    </div>
  )
})

export default NonInstructionalLesson
